import { PaginatedData, PaginationParams } from 'models/PaginatedData';
import httpClient from '../httpClient';
import {
  BuyerEnquiryHistory,
  Colleague,
  DealRoom,
  PublishDealRoomValues,
  TotalChatCount,
} from 'models/DealRoom';
import { PortfoilioLead } from 'api/portfolio/buyer';
import { Lead } from 'models/Lead';
import {
  Listing,
  ListingStage,
  ListingEnquiry,
  ListingType,
} from 'models/Listing';
import { AxiosRequestConfig } from 'axios';
import { DropzoneFile } from 'components/Dropzone/utils';
import {
  createFileFormData,
  getFileParams,
  getUploadedFile,
} from 'uploadFileUtils';
import { Note, NoteTag } from 'models/Note';
import { FilePreview, UploadedFileResponse } from 'models/File';
import { User } from 'models/User';
import { ReactionType, ReactionUsers } from 'models/Reactions';

function getRoom(dealRoomId: string, listingType?: ListingType) {
  return httpClient.get<DealRoom>(`deal-rooms/${dealRoomId}`, {
    params: { type: listingType },
  });
}

function updateDealRoom(
  dealRoomId: string,
  values: Partial<DealRoom>,
  isSuperAdmin?: boolean,
) {
  return httpClient.patch<DealRoom>(
    `deal-rooms${isSuperAdmin ? '/admin' : ''}/${dealRoomId}`,
    values,
  );
}

function createInitialDeal(data: any) {
  return httpClient.post<DealRoom>('deal-rooms', data);
}

function publishDeal(
  dealRoomId: string,
  values: Partial<PublishDealRoomValues>,
) {
  return httpClient.post<DealRoom>(`deal-rooms/${dealRoomId}/publish`, values);
}

function getDealsSeller(params?: PaginationParams & Record<string, any>) {
  return httpClient.get<PaginatedData<DealRoom>>(
    'portfolio/deal-rooms-seller',
    {
      params,
    },
  );
}

function getPortfolioLeads(params?: PaginationParams) {
  return httpClient.get<PaginatedData<PortfoilioLead>>(
    'portfolio/deal-rooms-buyer',
    {
      params,
    },
  );
}

function inviteLead(listingId: string, values: Partial<Lead>) {
  return httpClient.post<{ listingEnquiry: ListingEnquiry }>(
    `portfolio/listings/${listingId}/create-external-lead`,
    values,
  );
}

function changeListingStage(listingId: string, listingStage: ListingStage) {
  return httpClient.post(
    `portfolio/listings/${listingId}/change-listing-stage`,
    { listingStage },
  );
}

function invitePrivateViewer(listingId: string, values: any) {
  return httpClient.post<ListingEnquiry>(
    `portfolio/listings/${listingId}/invite-private-viewer`,
    values,
  );
}

async function uploadDirectDealFile(
  file: File,
  fileName: string,
  dealRoomId: string,
  config?: AxiosRequestConfig,
) {
  const formData = createFileFormData(fileName, file);

  return httpClient.post<{ file: DropzoneFile; dealRoom: DealRoom }>(
    `deal-rooms/${dealRoomId}/files`,
    formData,
    config,
  );
}

async function uploadDealFile(
  file: File,
  fileName: string,
  dealRoomId: string,
  config?: AxiosRequestConfig,
) {
  const fileParams = getFileParams(fileName, file);

  const { data } = await httpClient.post<
    UploadedFileResponse & { dealRoom: DealRoom }
  >(`deal-rooms/${dealRoomId}/upload-temporary-object`, fileParams);

  const { dealRoom, ...rest } = data;

  const uploadedFile = await getUploadedFile(rest, file, config);

  return { dealRoom, uploadedFile };
}

function deleteDealFile(fileName: string, fileId: string, dealRoomId: string) {
  return httpClient.delete<{ fileId: string; dealRoom: DealRoom }>(
    `deal-rooms/${dealRoomId}/files/${fileName}/${fileId}`,
  );
}

function toggleExternalContactUsage(
  listingId: string,
  useExternalListingContactInformation: boolean,
) {
  return httpClient.post<Listing>(
    `listings/${listingId}/setup-external-listing-contact`,
    { useExternalListingContactInformation },
  );
}

function deleteLead(leadId: string) {
  return httpClient.delete(`leads/${leadId}`);
}

function searchLeadBroker(searchValue: string) {
  return httpClient.get<string[]>(
    `deal-room-lookups/${searchValue}/lead-brokers`,
  );
}

function searchBrokerFirm(searchValue: string) {
  return httpClient.get<string[]>(
    `deal-room-lookups/${searchValue}/broker-firms`,
  );
}

function snoozeGetBuyers(id: string) {
  return httpClient.post(`deal-rooms/${id}/snooze-banner-notification`);
}

function getDealNotes(dealId: string, params: PaginationParams) {
  return httpClient.get<PaginatedData<Note>>(
    `deal-room-notes/deal-rooms/${dealId}`,
    {
      params,
    },
  );
}

function sendDealNote(
  dealId: string,
  content: string,
  tags: NoteTag[],
  uploadedFiles: DropzoneFile[],
) {
  return httpClient.post<Note>(`deal-room-notes/deal-rooms/${dealId}`, {
    content,
    tags,
    uploadedFiles,
  });
}

function removeDealNote(noteId: string) {
  return httpClient.post(`deal-room-notes/${noteId}/archive`);
}

function updateHasPremiumFeatures(
  dealRoomId: string,
  hasBossModeUnlockedPremiumFeatures: boolean,
) {
  return httpClient.post(
    `deal-rooms/admin/${dealRoomId}/set-unlocked-premium-feature`,
    { hasBossModeUnlockedPremiumFeatures },
  );
}

function getTotalChatCount(dealRoomId: string) {
  return httpClient.get<TotalChatCount>(`deal-rooms/${dealRoomId}/chat-info`);
}

function getColleagues(dealRoomId: string) {
  return httpClient.get<Array<Colleague>>(
    `deal-rooms/${dealRoomId}/colleagues`,
  );
}

function createColleagues(dealRoomId: string, colleaguesEmails: string[]) {
  return httpClient.post<Array<Colleague>>(
    `deal-rooms/${dealRoomId}/colleagues`,
    {
      colleagues: colleaguesEmails,
    },
  );
}

function removeColleague(dealRoomId: string, colleagueId: string) {
  return httpClient.delete<Array<Colleague>>(
    `deal-rooms/${dealRoomId}/colleagues/${colleagueId}`,
  );
}

function updateColleague(dealRoomId: string, colleagueId: string, data: any) {
  return httpClient.post<Array<Colleague>>(
    `deal-rooms/${dealRoomId}/colleagues/${colleagueId}/setup`,
    data,
  );
}

function remindColleague(dealRoomId: string, colleagueId: string) {
  return httpClient.post(
    `deal-rooms/${dealRoomId}/colleagues/${colleagueId}/send-reminder`,
  );
}

async function uploadDealNoteFile(
  dealRoomId: string,
  file: File,
  config?: AxiosRequestConfig,
): Promise<DropzoneFile | null> {
  const fileParams = getFileParams('dealRoomNoteFile', file);

  const { data } = await httpClient.post<UploadedFileResponse>(
    `deal-room-notes/deal-rooms/${dealRoomId}/upload-temporary-object`,
    fileParams,
  );

  return await getUploadedFile(data, file, config);
}

const moveFileToDataRoom = (noteId: string, fileId: string) => {
  return httpClient.post(
    `deal-room-notes/${noteId}/files/${fileId}/move-to-data-room`,
  );
};

const previewNoteFile = (noteId: string, fileId: string) => {
  return httpClient.get<FilePreview>(
    `deal-room-notes/${noteId}/files/${fileId}/preview`,
  );
};

const removeNoteFile = (
  noteId: string,
  fileId: string,
  deleteFromDataRoom?: boolean,
) => {
  return httpClient.post(
    `deal-room-notes/${noteId}/files/${fileId}/delete-file`,
    {
      deleteFromDataRoom,
    },
  );
};

const addNoteReaction = (noteId: string, reactionType: ReactionType) => {
  return httpClient.post<Note>(`deal-room-notes/${noteId}/reaction`, {
    reactionType,
  });
};

const getNoteReactions = (noteId: string) => {
  return httpClient.get<ReactionUsers>(`deal-room-notes/${noteId}/reactions`);
};

const getEnterpriseUsers = (dealRoomId: string) => {
  return httpClient.get<User[]>(
    `deal-rooms/${dealRoomId}/enterprise-colleagues`,
  );
};

const transferDealToEnterpriseUser = (dealRoomId: string, userId: string) => {
  return httpClient.post(`deal-rooms/${dealRoomId}/transfer-deal`, { userId });
};

const undoTransferDealToEnterpriseUser = (dealRoomId: string) => {
  return httpClient.post(`deal-rooms/${dealRoomId}/undo-transfer-deal`);
};

function sendPulseCheck(dealRoomId: string, leadIds: string[]) {
  return httpClient.post(`deal-rooms/${dealRoomId}/pulse-check`, { leadIds });
}

function getPreviousDealsLeads(
  dealRoomId: string,
  params?: PaginationParams & Record<string, any>,
) {
  return httpClient.get<PaginatedData<BuyerEnquiryHistory>>(
    `deal-rooms/${dealRoomId}/previous-deals-leads`,
    { params },
  );
}

export default {
  getRoom,
  updateDealRoom,
  createInitialDeal,
  publishDeal,
  getDealsSeller,
  getPortfolioLeads,
  inviteLead,
  changeListingStage,
  invitePrivateViewer,
  uploadDirectDealFile,
  uploadDealFile,
  deleteDealFile,
  toggleExternalContactUsage,
  deleteLead,
  searchLeadBroker,
  searchBrokerFirm,
  snoozeGetBuyers,
  getDealNotes,
  removeDealNote,
  sendDealNote,
  updateHasPremiumFeatures,
  getTotalChatCount,
  getColleagues,
  createColleagues,
  removeColleague,
  updateColleague,
  remindColleague,
  uploadDealNoteFile,
  moveFileToDataRoom,
  removeNoteFile,
  previewNoteFile,
  getEnterpriseUsers,
  transferDealToEnterpriseUser,
  undoTransferDealToEnterpriseUser,
  sendPulseCheck,
  addNoteReaction,
  getNoteReactions,
  getPreviousDealsLeads,
};
